/**
 * Login landing — Zitadel SSO redirect.
 *
 * No password form here anymore. Clicking "Sign in" redirects to Zitadel's
 * hosted UI at `https://dev-auth.axonbos.com/oauth/v2/authorize?...` which
 * handles login, register, email verification, forgot-password, and MFA
 * itself. After successful auth Zitadel redirects back to
 * `/desktop/auth/callback?code=...` and `<AuthCallback>` finalizes the
 * session.
 *
 * Single sign-on across the AxonBOS platform: the same Zitadel session
 * works for dev.axonbos.com, hros.axonbos.com, nzgourmet.axonbos.com, etc.
 */
import { useState } from 'react';
import { LogIn, UserPlus, Loader2 } from 'lucide-react';
import { startZitadelLogin, startZitadelRegister } from '@/lib/zitadel-oidc';

interface LoginProps {
  onLoggedIn: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function Login(_props: LoginProps) {
  const [busy, setBusy] = useState<'login' | 'register' | null>(null);

  const handleLogin = async () => {
    setBusy('login');
    try {
      await startZitadelLogin();
      // Redirect happens; this line won't run.
    } catch (e) {
      setBusy(null);
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const handleRegister = async () => {
    setBusy('register');
    try {
      await startZitadelRegister();
    } catch (e) {
      setBusy(null);
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-background text-foreground p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img
            src="/desktop/laabha.png"
            alt="Laabha"
            className="h-12 w-auto mx-auto mb-3"
            draggable={false}
          />
          <p className="text-sm text-muted-foreground mt-1">
            Sign in to Laabha
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 space-y-3 shadow-sm">
          <button
            onClick={handleLogin}
            disabled={busy !== null}
            className="w-full px-3 py-2 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy === 'login' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" /> Sign in
              </>
            )}
          </button>

          <button
            onClick={handleRegister}
            disabled={busy !== null}
            className="w-full px-3 py-2 text-sm rounded border border-border bg-background hover:bg-muted disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy === 'register' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" /> Create account
              </>
            )}
          </button>

          <p className="text-xs text-muted-foreground text-center pt-1">
            Sign-in, registration, email verification, forgot password and
            MFA are all handled by AxonBOS Identity. One login works across{' '}
            Laabha, YesBOS and HROS.
          </p>
        </div>

        <div className="text-xs text-center text-muted-foreground mt-4">
          By signing in you agree to AxonBOS Terms &amp; Privacy.
        </div>
      </div>
    </div>
  );
}
